import React from 'react';
import { FormControl, FormLabel, Input, Select, Box, Wrap, WrapItem, Tag, TagLabel, TagCloseButton, Text, Divider, HStack } from '@chakra-ui/react';
// import { Editor } from '@tinymce/tinymce-react';
import { Editor } from '@tinymce/tinymce-react';
import { useTasks } from '../../hooks/useTasks';
import { useOrganizationEnums } from '../../hooks/useOrganizationEnums';
import { formatEnumLabel } from '../../utils/organizationEnums';
import { parseTaskMentions, findTaskByIdentifier } from '../../utils/taskLinking';

interface TaskFormProps {
  formData: any;
  onFormChange: (data: Partial<any>) => void;
  users: any[];
}

const TaskForm: React.FC<TaskFormProps> = ({ formData, onFormChange, users }) => {
  const { data: organizationEnums } = useOrganizationEnums();
  const { data: allTasks } = useTasks(true);
  
  // User selection logic
  const handleUserToggle = (userId: string) => {
    const currentAuthBy = formData.authBy || [];
    const isSelected = currentAuthBy.includes(userId);
    if (isSelected) {
      onFormChange({ authBy: currentAuthBy.filter((id: string) => id !== userId) });
    } else {
      onFormChange({ authBy: [...currentAuthBy, userId] });
    }
  };

  const getSelectedUserNames = () => {
    if (!formData.authBy || formData.authBy.length === 0) return [];
    return formData.authBy.map((userId: string) => {
      const user = users?.find((u: any) => u._id === userId);
      return user ? `${user.firstName} ${user.lastName}` : 'Unknown User';
    });
  };

  return (
    <React.Fragment>
      
      <Text fontSize="md" fontWeight="bold" color="gray.600" mb={2} bg={'gray.100'} w={'100%'} p={3}>
        Task Basic Information
      </Text>
      <FormControl isRequired>
        <FormLabel>Task Title</FormLabel>
        <Input
          value={formData.taskTitle || ''}
          onChange={e => onFormChange({ taskTitle: e.target.value })}
          placeholder="Enter task title"
        />
      </FormControl>
      <FormControl isRequired>
        <FormLabel>Description</FormLabel>
        <Editor
          apiKey="gv5v4u4161l1zg7cl5lb9xtexo16h6aayuag1bpjerkz4wgl"
          value={formData.description || ''}
          init={{
            height: 270,
            menubar: false,
            plugins: [
                  'link', 'lists', 'code', 'autolink', 'charmap', 'table', 'paste', 'help', 'quickbars'
            ],
            toolbar:
              'undo redo | formatselect | bold italic underline forecolor | link | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | removeformat | help',
            branding: false,
            statusbar: false,
            // Auto-detect URLs and convert them to links
            autolink_pattern: /^(https?:\/\/|www\.|mailto:|tel:)/,
            // Make links open in new tab by default
            link_default_target: '_blank',
            // Allow quick inline link creation
            link_context_toolbar: true,
            // Auto-convert URLs to links when typing
            convert_urls: true,
            // Setup for automatic link detection and task mentions
            setup: (editor: any) => {
              let isProcessing = false;
              
              // Helper function to convert URLs to links without duplication
              const convertUrlsToLinks = (content: string) => {
                // Only convert plain URLs that aren't already inside link tags
                const urlRegex = /(?<!<a[^>]*>)(?<!href=["'])(?<!href=)https?:\/\/[^\s<>"{}|\\^`[\]]+(?![^<]*<\/a>)/g;
                return content.replace(urlRegex, '<a href="$&" target="_blank">$&</a>');
              };
              
              // Helper function to convert task mentions to clickable links
              const convertTaskMentions = (content: string) => {
                if (!allTasks) return content;
                
                // Pattern to match task references: #TASK-123, @task:id, or PP-123
                const taskMentionPattern = /(?:#([A-Z]{2,}-[A-Z0-9-]+)|@task:([a-zA-Z0-9]+)|([A-Z]{2,}-[A-Z0-9-]+))/g;
                
                return content.replace(taskMentionPattern, (match, hashTaskMatch, mongoIdMatch, plainTaskMatch) => {
                  let taskId: string;
                  let type: 'taskId' | 'mongoId' | 'projectNumber';
                  
                  if (hashTaskMatch) {
                    taskId = hashTaskMatch;
                    type = 'taskId';
                  } else if (mongoIdMatch) {
                    taskId = mongoIdMatch;
                    type = 'mongoId';
                  } else if (plainTaskMatch) {
                    taskId = plainTaskMatch;
                    type = 'projectNumber';
                  } else {
                    return match;
                  }
                  
                  // Find the task
                  const task = findTaskByIdentifier(allTasks, taskId, type);
                  
                  if (!task) {
                    return `<span class="task-mention-not-found" style="color: #999; text-decoration: line-through; background-color: #f5f5f5; padding: 2px 4px; border-radius: 3px;" title="Task not found">${match}</span>`;
                  }
                  
                  return `<span class="task-mention" style="background-color: #e3f2fd; color: #1976d2; padding: 2px 6px; border-radius: 4px; cursor: pointer; text-decoration: none; font-weight: 500;" data-task-id="${task._id}" title="${task.taskTitle} (${task.status})">${match}</span>`;
                });
              };
              
              // Handle paste events to auto-linkify URLs and task mentions
              editor.on('paste', (e: any) => {
                if (isProcessing) return;
                
                setTimeout(() => {
                  isProcessing = true;
                  const content = editor.getContent();
                  let newContent = convertUrlsToLinks(content);
                  newContent = convertTaskMentions(newContent);
                  
                  if (newContent !== content) {
                    editor.setContent(newContent);
                  }
                  isProcessing = false;
                }, 50);
              });
              
              // Auto-detect URLs and task mentions when user stops typing (debounced)
              let typingTimer: NodeJS.Timeout;
              editor.on('keyup', () => {
                if (isProcessing) return;
                
                clearTimeout(typingTimer);
                typingTimer = setTimeout(() => {
                  isProcessing = true;
                  const content = editor.getContent();
                  let newContent = convertUrlsToLinks(content);
                  newContent = convertTaskMentions(newContent);
                  
                  if (newContent !== content) {
                    const cursorPos = editor.selection.getBookmark();
                    editor.setContent(newContent);
                    editor.selection.moveToBookmark(cursorPos);
                  }
                  isProcessing = false;
                }, 1000); // Wait 1 second after user stops typing
              });
              
              // Handle clicks on task mentions
              editor.on('click', (e: any) => {
                const target = e.target;
                if (target.classList.contains('task-mention')) {
                  const taskId = target.getAttribute('data-task-id');
                  if (taskId) {
                    // Open task in new tab/window
                    window.open(`/dashboard/task/${taskId}`, '_blank');
                  }
                }
              });
              
              // Add autocomplete hint for task mentions when typing #
              editor.on('keydown', (e: any) => {
                if (e.key === '#') {
                  // Show task suggestion hint
                  setTimeout(() => {
                    console.log('💡 Tip: Type #p to see all tasks, or continue typing to filter (e.g., #PP-123456)');
                  }, 100);
                }
              });
            },
          }}
          onEditorChange={content => onFormChange({ description: content })}
        />
      </FormControl>

      <HStack spacing={4} align="flex-start" flexWrap="wrap" w={'100%'}>
        <HStack w={'49%'}>
      <FormControl isRequired>
        <FormLabel>Type</FormLabel>
        <Select value={formData.type} onChange={e => onFormChange({ type: e.target.value })}>
          {organizationEnums?.taskTypes.map((type) => (
            <option key={type} value={type}>
              {formatEnumLabel(type)}
            </option>
          ))}
        </Select>
      </FormControl>
      </HStack>

      {/* Specification field removed from TaskForm, now on Project */}

        <HStack w={'49%'}>
      <FormControl>
        <FormLabel>Authorized By (Select Users)</FormLabel>
        {/* User Selection Dropdown */}
        <Select
          placeholder="Select users to authorize this task..."
          onChange={e => {
            if (e.target.value) {
              handleUserToggle(e.target.value);
              e.target.value = '';
            }
          }}
        >
          {users?.map((user: any) => {
            const isSelected = formData.authBy?.includes(user._id);
            return (
              <option key={user._id} value={user._id} disabled={isSelected}>
                {`${user.firstName} ${user.lastName}`} {isSelected ? '(Selected)' : ''}
              </option>
            );
          })}
        </Select>
        <Text fontSize="xs" color="gray.500" mt={1}>
          You can select multiple users who have authorized this task
        </Text>
        {/* Selected Users Display */}
        {formData.authBy && formData.authBy.length > 0 && (
          <Box mb={3}>
            <Text fontSize="sm" color="gray.600" mb={2}>
              Selected Users:
            </Text>
            <Wrap spacing={2}>
              {getSelectedUserNames().map((userName: string, index: number) => (
                <WrapItem key={index}>
                  <Tag size="sm" colorScheme="green" variant="solid">
                    <TagLabel>{userName}</TagLabel>
                    <TagCloseButton onClick={() => handleUserToggle(formData.authBy![index])} />
                  </Tag>
                </WrapItem>
              ))}
            </Wrap>
          </Box>
        )}
      </FormControl>

      </HStack>
      </HStack>


      <Divider my={4} />

      <Text fontSize="md" fontWeight="bold" color="gray.600" mb={2} bg={'gray.100'} w={'100%'} p={3}>
        Price Settings
      </Text>
      <HStack spacing={4} align="flex-start" flexWrap="wrap" w={'100%'}>
        <HStack w={'49%'}>
          <FormControl>
            <FormLabel>Quoted Price (£)</FormLabel>
            <Input
              type="number"
              value={formData.quotedPrice === undefined || formData.quotedPrice === null ? '' : formData.quotedPrice}
              onChange={e => {
                const val = e.target.value;
                onFormChange({
                  quotedPrice: val === '' ? undefined : Number(val),
                });
              }}
              placeholder="Enter quoted price (optional)"
            />
          </FormControl>
        </HStack>
        <HStack w={'49%'}>
          <FormControl>
            <FormLabel>Agreed Price (£)</FormLabel>
            <Input
              type="number"
              value={formData.agreedPrice === undefined || formData.agreedPrice === null ? '' : formData.agreedPrice}
              onChange={e => {
                const val = e.target.value;
                onFormChange({
                  agreedPrice: val === '' ? undefined : Number(val),
                });
              }}
              placeholder="Enter agreed price (optional)"
            />
          </FormControl>
        </HStack>

        </HStack>
        
        <Divider my={4} />

      <Text fontSize="md" fontWeight="bold" color="gray.600" mb={2} bg={'gray.100'} w={'100%'} p={3}>
        Date Settings
      </Text>
      <HStack spacing={4} align="flex-start" flexWrap="wrap" w={'100%'}>
        <HStack w={'32%'}>
          <FormControl>
            <FormLabel>Date Requested</FormLabel>
            <Input
              type="date"
              value={
                typeof formData.dateRequested === 'string'
                  ? formData.dateRequested.includes('T')
                    ? formData.dateRequested.split('T')[0]
                    : formData.dateRequested
                  : ''
              }
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                onFormChange({ dateRequested: e.target.value });
              }}
            />
          </FormControl>
        </HStack>
        <HStack w={'32%'}>
          <FormControl>
            <FormLabel>Start Date</FormLabel>
            <Input
              type="date"
              value={
                typeof formData.dateStarted === 'string'
                  ? formData.dateStarted.includes('T')
                    ? formData.dateStarted.split('T')[0]
                    : formData.dateStarted
                  : ''
              }
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                onFormChange({ dateStarted: e.target.value });
              }}
            />
          </FormControl>
        </HStack>
        <HStack w={'32%'}>
          <FormControl>
            <FormLabel>Due Date</FormLabel>
            <Input
              type="date"
              value={
                typeof formData.dueDate === 'string'
                  ? formData.dueDate.includes('T')
                    ? formData.dueDate.split('T')[0]
                    : formData.dueDate
                  : ''
              }
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                onFormChange({ dueDate: e.target.value });
              }}
            />
          </FormControl>
        </HStack>
      </HStack>
      <Divider my={4} />
      <Text fontSize="md" fontWeight="bold" color="gray.600" mb={2} bg={'gray.100'} w={'100%'} p={3}>
        Task Settings
      </Text>

      <HStack spacing={4} align="flex-start" flexWrap="wrap" w={'100%'}>
        <HStack w={'32%'} >
          <FormControl>
            <FormLabel>Priority</FormLabel>
            <Select
              value={formData.priority}
              onChange={e => onFormChange({ priority: e.target.value })}
            >
              {organizationEnums?.taskPriorities.map((priority) => (
                <option key={priority} value={priority}>
                  {formatEnumLabel(priority)}
                </option>
              ))}
            </Select>
          </FormControl>
        </HStack>
        <HStack w={'32%'} >
          <FormControl>
            <FormLabel>Status</FormLabel>
            <Select value={formData.status} onChange={e => onFormChange({ status: e.target.value })}>
              {organizationEnums?.taskStatuses.map((status) => (
                <option key={status} value={status}>
                  {formatEnumLabel(status)}
                </option>
              ))}
            </Select>
          </FormControl>
        </HStack>
        <HStack w={'32%'} >
          <FormControl>
            <FormLabel>Assign To User</FormLabel>
            {/* Dropdown for adding users */}
            <Select
              placeholder="Add user to assign..."
              value=""
              onChange={e => {
                const value = e.target.value;
                if (value && !formData.assignedToUser?.includes(value)) {
                  onFormChange({ assignedToUser: [...(formData.assignedToUser || []), value] });
                }
              }}
            >
              {users?.filter((user: any) => !formData.assignedToUser?.includes(user._id)).map((user: any) => (
                <option key={user._id} value={user._id}>
                  {user.firstName} {user.lastName}
                </option>
              ))}
            </Select>
            <Text fontSize="xs" color="gray.500" mt={1}>
              Assign this task to one or more users (optional)
            </Text>
            {/* Selected Users as Tags */}
            {formData.assignedToUser && formData.assignedToUser.length > 0 && (
              <Wrap mb={2}>
                {formData.assignedToUser.map((userId: string) => {
                  const user = users?.find((u: any) => u._id === userId);
                  return user ? (
                    <WrapItem key={userId}>
                      <Tag size="md" colorScheme="blue" borderRadius="full">
                        <TagLabel>{user.firstName} {user.lastName}</TagLabel>
                        <TagCloseButton onClick={() => {
                          const updated = formData.assignedToUser!.filter((id: string) => id !== userId);
                          onFormChange({ assignedToUser: updated });
                        }} />
                      </Tag>
                    </WrapItem>
                  ) : null;
                })}
              </Wrap>
            )}
          </FormControl>
        </HStack>
      </HStack>
    </React.Fragment>
  );
};

export default TaskForm;
